import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { PencilIcon, DuplicateIcon, TrashIcon } from '@heroicons/react/outline'
import { UserContext } from '../../../hooks/UserContext'
import { isAdminTierUser } from '../../../utils/userUtils'
import { BasicTable, Button, ConfirmDialog, Notification, PageView, PageTopBar } from '../../../components'
import { Loader } from '../../../components/Loader'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { fetchDefaultCampaigns, createDefaultCampaign, updateDefaultCampaign, deleteDefaultCampaign } from '../../../services'
import { CampaignFormModal } from './CampaignFormModal'
import { COUNTRIES, YEARLY_GROUP, groupByYear, formatDate, formatTimestamp, getErrorMessage, isUnprocessable } from './utils'

const T_PREFIX = 'campaigns'
const ICON_BUTTON = 'p-1 text-gray-500 transition-colors'

export const CampaignsContainer = () => {
  const { i18n } = useTranslation()
  const lang = i18n.language?.slice(0, 2) || 'es'
  const { warehouse_id } = useParams()
  const queryClient = useQueryClient()
  const { user } = useContext(UserContext)

  // Reading needs the admin tier (the route is gated the same way); writing is whatever the API says.
  const hasAccess = isAdminTierUser(user)

  const [country, setCountry] = useState(null)
  const [formModal, setFormModal] = useState({ open: false, mode: 'create', initial: null })
  const [confirmDelete, setConfirmDelete] = useState({ open: false, season: null })
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [serverError, setServerError] = useState(null)

  const showNotification = (type, title) => setNotification({ show: true, type, title })

  const { data: warehouse, status: warehouseStatus } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    enabled: hasAccess && !!warehouse_id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // The user's pick wins; the warehouse only fills the initial value.
  const effectiveCountry = country || (COUNTRIES.includes(warehouse?.country) ? warehouse.country : 'MX')

  // Wait for the warehouse to settle before the first fetch so the initial request is for its country, not the fallback.
  const warehouseSettled = !warehouse_id || warehouseStatus === 'success' || warehouseStatus === 'error'

  const { data, isError, refetch } = useQuery(['metrics_default_campaigns', effectiveCountry], () => fetchDefaultCampaigns(effectiveCountry), {
    enabled: hasAccess && (!!country || warehouseSettled),
    refetchOnWindowFocus: false,
    // A 4xx will not heal by itself (e.g. the API is not deployed yet): fail at once, retry only server errors.
    retry: (count, error) => !(error?.response?.status < 500) && count < 3,
  })

  const canManage = data?.can_write === true
  const groups = useMemo(() => groupByYear(data?.seasons || []), [data])
  const countryLabel = i18n.t(`${T_PREFIX}.countries.${effectiveCountry}`)

  const closeForm = () => {
    setFormModal(prev => ({ ...prev, open: false }))
    setServerError(null)
  }

  const onWriteSuccess = key => {
    queryClient.invalidateQueries(['metrics_default_campaigns'])
    closeForm()
    showNotification('success', i18n.t(`${T_PREFIX}.notifications.${key}`))
  }

  // A 422 belongs to the form (duplicate default, bad dates); anything else is a toast.
  const onWriteError = error => {
    if (isUnprocessable(error) && getErrorMessage(error)) {
      setServerError(getErrorMessage(error))
      return
    }
    showNotification('error', getErrorMessage(error) || i18n.t(`${T_PREFIX}.errors.generic`))
  }

  // The row vanished or the user lost the right to touch it: the list is stale, so refresh it and say so in our own words
  // (the server bodies are English or a raw ActiveRecord message).
  const isGoneOrForbidden = error => [403, 404].includes(error?.response?.status)

  const statusMessage = (error, fallbackKey) => {
    const status = error?.response?.status
    if (status === 403) return i18n.t(`${T_PREFIX}.errors.forbidden`)
    if (status === 404) return i18n.t(`${T_PREFIX}.errors.gone`)
    return getErrorMessage(error) || i18n.t(`${T_PREFIX}.errors.${fallbackKey}`)
  }

  const onUpdateError = error => {
    if (isGoneOrForbidden(error)) {
      queryClient.invalidateQueries(['metrics_default_campaigns'])
      closeForm()
      showNotification('error', statusMessage(error, 'generic'))
      return
    }
    onWriteError(error)
  }

  const onDeleteError = error => {
    if (isGoneOrForbidden(error)) queryClient.invalidateQueries(['metrics_default_campaigns'])
    showNotification('error', statusMessage(error, 'delete_generic'))
  }

  const createMutation = useMutation(createDefaultCampaign, { onSuccess: () => onWriteSuccess('created'), onError: onWriteError })
  const updateMutation = useMutation(({ id, payload }) => updateDefaultCampaign(id, payload), {
    onSuccess: () => onWriteSuccess('updated'),
    onError: onUpdateError,
  })
  const deleteMutation = useMutation(deleteDefaultCampaign, {
    onSuccess: () => {
      queryClient.invalidateQueries(['metrics_default_campaigns'])
      showNotification('success', i18n.t(`${T_PREFIX}.notifications.deleted`))
    },
    onError: onDeleteError,
    onSettled: () => setConfirmDelete({ open: false, season: null }),
  })

  const openForm = (mode, initial = null) => {
    setServerError(null)
    setFormModal({ open: true, mode, initial })
  }

  const handleSubmit = payload => {
    if (formModal.mode === 'edit') updateMutation.mutate({ id: formModal.initial.id, payload })
    else createMutation.mutate(payload)
  }

  const columns = useMemo(
    () => [
      { Header: i18n.t(`${T_PREFIX}.headers.name`), accessor: 'name', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.starts_on`), accessor: 'starts_on', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.ends_on`), accessor: 'ends_on', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.recurrence`), accessor: 'recurrence', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.updated_at`), accessor: 'updated_at', disableSortBy: true },
      { Header: i18n.t(`${T_PREFIX}.headers.actions`), accessor: 'actions', disableSortBy: true },
    ],
    [i18n],
  )

  const rowsOf = rows =>
    rows.map(season => ({
      name: season.name,
      starts_on: formatDate(season.starts_on, lang),
      ends_on: formatDate(season.ends_on, lang),
      recurrence: i18n.t(`${T_PREFIX}.recurrence.${season.recurrence}`),
      updated_at: formatTimestamp(season.updated_at, lang),
      actions: canManage ? (
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => openForm('edit', season)}
            className={`${ICON_BUTTON} hover:text-indigo-600`}
            title={i18n.t(`${T_PREFIX}.actions.edit`)}
            aria-label={i18n.t(`${T_PREFIX}.actions.edit`)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => openForm('duplicate', season)}
            className={`${ICON_BUTTON} hover:text-blue-600`}
            title={i18n.t(`${T_PREFIX}.actions.duplicate`)}
            aria-label={i18n.t(`${T_PREFIX}.actions.duplicate`)}
          >
            <DuplicateIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete({ open: true, season })}
            className={`${ICON_BUTTON} hover:text-red-600`}
            title={i18n.t(`${T_PREFIX}.actions.delete`)}
            aria-label={i18n.t(`${T_PREFIX}.actions.delete`)}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ) : null,
    }))

  const topBar = (
    <PageTopBar>
      <div>
        <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
        <div className="text-sm text-gray-500">{i18n.t(`${T_PREFIX}.subtitle`)}</div>
      </div>
      {hasAccess && (
        <div className="flex items-center gap-2 ml-auto">
          <select
            className="border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={effectiveCountry}
            onChange={e => setCountry(e.target.value)}
            aria-label={i18n.t(`${T_PREFIX}.country_label`)}
          >
            {COUNTRIES.map(c => (
              <option key={c} value={c}>
                {i18n.t(`${T_PREFIX}.countries.${c}`)}
              </option>
            ))}
          </select>
          {canManage && (
            <Button type="primary" onClick={() => openForm('create')}>
              {i18n.t(`${T_PREFIX}.actions.create`)}
            </Button>
          )}
        </div>
      )}
    </PageTopBar>
  )

  if (!hasAccess) {
    return (
      <PageView topMenu={topBar}>
        <div className="text-center py-12 text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</div>
      </PageView>
    )
  }

  return (
    <PageView topMenu={topBar}>
      {!data && !isError && <Loader show />}
      {isError && !data && (
        <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {i18n.t(`${T_PREFIX}.errors.load`)}
          <button type="button" onClick={() => refetch()} className="underline">
            {i18n.t(`${T_PREFIX}.errors.retry`)}
          </button>
        </div>
      )}
      {data && groups.length === 0 && <div className="text-center py-12 text-gray-500">{i18n.t(`${T_PREFIX}.empty`, { country: countryLabel })}</div>}
      {groups.map(group => (
        <section key={group.key} className="mb-6">
          <h3 className="mb-2 text-base font-semibold text-gray-900">{group.key === YEARLY_GROUP ? i18n.t(`${T_PREFIX}.yearly_group`) : group.year}</h3>
          <BasicTable columns={columns} data={rowsOf(group.rows)} showPaginator={false} />
        </section>
      ))}
      <CampaignFormModal
        open={formModal.open}
        setOpen={open => (open ? setFormModal(prev => ({ ...prev, open })) : closeForm())}
        mode={formModal.mode}
        country={effectiveCountry}
        initial={formModal.initial}
        onSubmit={handleSubmit}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        serverError={serverError}
        onClearServerError={() => setServerError(null)}
      />
      <ConfirmDialog
        open={confirmDelete.open}
        setOpen={open => setConfirmDelete(prev => ({ ...prev, open }))}
        title={i18n.t(`${T_PREFIX}.confirm_delete.title`)}
        description={
          confirmDelete.season
            ? i18n.t(`${T_PREFIX}.confirm_delete.description`, {
                name: confirmDelete.season.name,
                starts_on: formatDate(confirmDelete.season.starts_on, lang),
                ends_on: formatDate(confirmDelete.season.ends_on, lang),
                country: i18n.t(`${T_PREFIX}.countries.${confirmDelete.season.country}`),
              })
            : ''
        }
        confirmLabel={i18n.t(`${T_PREFIX}.confirm_delete.confirm`)}
        cancelLabel={i18n.t(`${T_PREFIX}.confirm_delete.cancel`)}
        onConfirm={() => confirmDelete.season && deleteMutation.mutate(confirmDelete.season.id)}
        loading={deleteMutation.isLoading}
        type="danger"
      />
      <Notification
        show={notification.show}
        setShow={show => setNotification(prev => ({ ...prev, show }))}
        type={notification.type}
        title={notification.title}
      />
    </PageView>
  )
}
